/**
 * ============================================================================
 * KbPageLinkHydrator — Enriquece <a data-kb-page-link> renderizados pelo
 *                      editor/reader com dados ao vivo do resolver:
 *                        - Sobrescreve href com a rota real (react-router)
 *                        - Sobrescreve o texto quando vier do título atual*
 *                        - Aplica data-kb-state para CSS/badge visual
 *                        - Adiciona tooltip (title) com breadcrumb/status
 *                        - Delegate clique → navegação SPA
 *
 * (*) O texto do link no editor é preservado (o usuário pode ter customizado).
 *     Só o `title` (tooltip) e o `data-kb-state` são atualizados. O `<a>` NÃO
 *     é substituído — mantemos o texto original do doc como fonte de verdade.
 *
 * CHANGELOG:
 *   RELEASE: 2026-07-08 v1.0.0 - Versão inicial.
 * ============================================================================
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useKbPageLinkResolver, type KbResolvedLink } from "@/hooks/kb/useKbPageLinkResolver";

interface Props {
  /** Container onde os <a data-kb-page-link> vivem. */
  containerRef: React.RefObject<HTMLElement>;
  /** Se true, links abrem em nova aba por padrão (útil no reader público). */
  openInNewTab?: boolean;
  /** Reexecuta a varredura quando essa key muda (ex.: contentJson). */
  observeKey?: unknown;
}

const STATE_LABEL: Record<KbResolvedLink["state"], string> = {
  ok: "",
  no_access: "sem acesso",
  archived: "arquivado",
  removed: "removido",
  draft: "rascunho",
  in_review: "em revisão",
};

export function KbPageLinkHydrator({ containerRef, openInNewTab, observeKey }: Props) {
  const [pageIds, setPageIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const params = useParams();

  // Varre o container e coleta pageIds únicos.
  const scan = () => {
    const root = containerRef.current;
    if (!root) return;
    const anchors = Array.from(root.querySelectorAll<HTMLAnchorElement>("a[data-kb-page-link]"));
    const ids = Array.from(new Set(anchors.map((a) => a.getAttribute("data-page-id") ?? "").filter(Boolean)));
    setPageIds((prev) => {
      if (prev.length === ids.length && prev.every((v, i) => v === ids[i])) return prev;
      return ids;
    });
  };

  // Observa mutações do DOM do container (edição ao vivo).
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    scan();
    const mo = new MutationObserver(() => scan());
    mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-page-id", "data-anchor"] });
    return () => mo.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current, observeKey]);

  const { data: resolved } = useKbPageLinkResolver(pageIds);

  // Aplica atributos ao DOM sempre que resolver responder ou anchors mudarem.
  useEffect(() => {
    const root = containerRef.current;
    if (!root || !resolved) return;
    const anchors = Array.from(root.querySelectorAll<HTMLAnchorElement>("a[data-kb-page-link]"));
    anchors.forEach((a) => {
      const pid = a.getAttribute("data-page-id");
      if (!pid) return;
      const info = resolved.get(pid);
      const anchor = a.getAttribute("data-anchor");

      if (!info) {
        a.setAttribute("data-kb-state", "loading");
        return;
      }

      a.setAttribute("data-kb-state", info.state);

      // href real (com fallback para "#" quando removido/sem acesso)
      if (info.has_access && info.slug && info.space_slug) {
        const spaceId = info.space_id ?? "";
        const base = `/tenant/kb/${spaceId}/pages/${pid}`;
        a.setAttribute("href", anchor ? `${base}#${anchor}` : base);
      } else {
        a.setAttribute("href", "#");
      }

      // tooltip
      const parts: string[] = [];
      if (info.title) parts.push(info.title);
      if (info.space_name) parts.push(info.space_name);
      if (info.collection_name) parts.push(info.collection_name);
      if (STATE_LABEL[info.state]) parts.push(`(${STATE_LABEL[info.state]})`);
      a.setAttribute("title", parts.join(" › "));

      // Se o link estava sem texto (inserido pelo picker sem seleção prévia),
      // popula com o snapshot ou título atual como fallback visual.
      if (a.textContent === "" || a.textContent === null) {
        const snapshot = a.getAttribute("data-snapshot-title");
        a.textContent = info.title ?? snapshot ?? "Artigo";
      }
    });
  }, [resolved, containerRef, pageIds]);

  // Event delegation: intercepta cliques em <a data-kb-page-link> para navegar via SPA.
  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const handler = (e: MouseEvent) => {
      const target = (e.target as HTMLElement | null)?.closest?.("a[data-kb-page-link]") as HTMLAnchorElement | null;
      if (!target) return;
      const pid = target.getAttribute("data-page-id");
      if (!pid) return;
      const info = resolved?.get(pid);
      if (!info || !info.has_access || !info.slug) {
        e.preventDefault();
        return;
      }
      // Ctrl/Cmd/aux → deixa comportamento padrão (nova aba)
      if (e.metaKey || e.ctrlKey || e.button === 1 || openInNewTab) return;
      e.preventDefault();
      const anchor = target.getAttribute("data-anchor");
      const spaceId = info.space_id ?? "";
      const path = `/tenant/kb/${spaceId}/pages/${pid}${anchor ? `#${anchor}` : ""}`;
      navigate(path);
    };
    root.addEventListener("click", handler);
    return () => root.removeEventListener("click", handler);
  }, [resolved, navigate, openInNewTab, containerRef]);

  return null;
}
